import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

/**
 * Datadog Browser RUM の初期化（Legacy Vue 2.6 / Laravel Mix 側）。
 *
 * applicationId / clientToken が env（MIX_ プレフィックス）に無い場合は何もしない。
 * （ローカル/dev/stg/preview環境で誤送信しないため）。
 *
 * 必要な env:
 *   MIX_DD_RUM_APPLICATION_ID
 *   MIX_DD_RUM_CLIENT_TOKEN
 *   MIX_DD_SITE                  (例: datadoghq.com)
 *   MIX_OBSERVABILITY_ENV        (環境名)
 *   MIX_DD_VERSION               (git sha 等)
 */
export function initDatadogRum() {
    const applicationId = process.env.MIX_DD_RUM_APPLICATION_ID;
    const clientToken = process.env.MIX_DD_RUM_CLIENT_TOKEN;

    if (!applicationId || !clientToken) {
        return;
    }

    datadogRum.init({
        applicationId,
        clientToken,
        site: process.env.MIX_DD_SITE || "datadoghq.com",
        service: "fe-legacy",
        env: process.env.MIX_OBSERVABILITY_ENV,
        version: process.env.MIX_DD_VERSION || undefined,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0, // コスト制限のため停止
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true, // クリック/スクロールのヒートマップに必要なユーザー操作トラッキング。
        // FE → BE の distributed trace を 1 本に繋ぐため、同一オリジンの API へ trace ヘッダーを伝播する。
        // BE は OTel の W3C TraceContext（traceparent）で受けるため tracecontext を明示（datadog 形式も RUM 連携用に併送）。
        // 対象は同一オリジンのみ（外部ドメインへトレースヘッダーを送らない）。
        allowedTracingUrls: [{ match: window.location.origin, propagatorTypes: ["tracecontext", "datadog"] }],
        traceSampleRate: 100,// ヘッダー注入するリクエストの割合（追跡中セッションが対象）
        defaultPrivacyLevel: "mask-user-input",
    });
}

/**
 * Datadog Browser Logs の初期化（Legacy Vue 2.6 / Laravel Mix 側）。
 *
 * RUM SDK は RUM イベント（view / action / resource / error）しか送らず Log Explorer には
 * 何も届かないため、ブラウザのログを Datadog Logs に載せるにはこの SDK が別途必要。
 * clientToken は RUM と共通（applicationId は Logs では不要）。
 *
 * initDatadogRum() の後に呼ぶこと。RUM が先に init されていると各ログに
 * session_id / view.id が自動付与され、Log Explorer から RUM セッションへ辿れる。
 */
export function initDatadogLogs() {
    const clientToken = process.env.MIX_DD_RUM_CLIENT_TOKEN;

    if (!clientToken) {
        return;
    }

    datadogLogs.init({
        clientToken,
        site: process.env.MIX_DD_SITE || "datadoghq.com",
        service: "fe-legacy", // RUM と同じ service で揃える（Log ↔ RUM ↔ APM を service タグで突き合わせる）。
        env: process.env.MIX_OBSERVABILITY_ENV,
        version: process.env.MIX_DD_VERSION || undefined,
        forwardErrorsToLogs: true, // 未捕捉例外・unhandled rejection・失敗した XHR/fetch を error ログとして送る。
        forwardConsoleLogs: ["error"], // console は error のみ転送する（warn / log まで含めると Vue の警告で量が膨らむ）。
        sessionSampleRate: 100, // ログは全セッション収集する（RUM の sessionSampleRate とは独立した設定）。
    });
}
